import type { NonConformity } from './non-conformity';
import type { ChecklistItem, ServiceChecklist } from './service-checklist';
import {
  nonConformitiesOfAnswer,
  type ServiceVerification,
  type VerificationAnswer,
} from './service-verification';

/**
 * A ficha de verificação do serviço, pavimento por pavimento (Fase 24, RN-298 e RN-299).
 *
 * ```text
 * FORM.109 · Execução de Alvenaria Não Estrutural · Amostragem: 40%
 * Obra · Serviço · Executado por · Responsável pela inspeção
 *
 *                                  1º Pav.            2º Pav.
 *                              Aprovado  Reprovado  Aprovado  Reprovado
 * Marcação da Primeira Fiada   10/09/26             12/09/26
 * Espessura da Junta                      NC 48      12/09/26
 *
 * Considerações (locais pendentes de inspeção): 3º Pav., 4º Pav.
 * ```
 *
 * É a FVS do cliente — "FORM.109 · Execução de Alvenaria Não Estrutural" — montada a partir
 * do que já existe: as alocações conferidas do serviço, as verificações (`0020`) e as NCs.
 * Nada é gravado aqui, e nenhum número nasce só nesta tela: tudo se **deriva**, como o RDO
 * deriva a seção de qualidade (ADR-128).
 *
 * ## Os locais são os pavimentos onde o serviço foi executado (RN-298)
 *
 * Não os da obra inteira: uma ficha com os doze pavimentos da torre, oito deles em branco,
 * esconderia o que importa — onde se trabalhou e ainda não se inspecionou. Um local entra
 * quando há quantidade executada **conferida** daquele serviço na frente, e a ordem é a da
 * torre, de baixo para cima.
 *
 * ## Três blocos, uma leitura
 *
 * 1. **Execuções por dia** — dia × pavimento, com a quantidade e o resultado da inspeção
 *    daquele dia e daquela frente: aprovado, reprovado (com o número da NC) ou pendente;
 * 2. **A ficha** — itens × pavimentos. Em *Aprovado* vai a data da verificação que aprovou o
 *    item; em *Reprovado*, o número da NC. A cancelada não entra: é a linha riscada;
 * 3. **A situação do pavimento** — conforme, não conforme ou pendente (RN-299).
 *
 * Função pura, sem I/O: recebe o que o caso de uso leu e devolve a ficha. É o que permite
 * testar a ficha com aprovado, reprovado e pendente sem banco.
 */

export type LocalStatus = 'CONFORME' | 'NAO_CONFORME' | 'PENDENTE';

export const LOCAL_STATUS_LABEL: Readonly<Record<LocalStatus, string>> = {
  CONFORME: 'Conforme',
  NAO_CONFORME: 'Não conforme',
  PENDENTE: 'Pendente',
};

/** O resultado da inspeção de um dia numa frente (bloco 1). */
export type DayInspectionResult = 'APROVADO' | 'REPROVADO' | 'PENDENTE';

export const DAY_INSPECTION_RESULT_LABEL: Readonly<Record<DayInspectionResult, string>> = {
  APROVADO: 'Aprovado',
  REPROVADO: 'Reprovado',
  PENDENTE: 'Pendente de inspeção',
};

/** A frente da obra, com o pavimento e a torre onde ela fica — o que dá o nome e a ordem. */
export interface SheetFront {
  readonly activityId: string;
  readonly towerName: string;
  readonly towerPosition: number;
  readonly floorName: string;
  /** O nível do pavimento: o subsolo é negativo, o térreo é zero. */
  readonly level: number;
}

/** Uma execução conferida: a pessoa, o dia, a frente e quanto saiu daquele serviço. */
export interface SheetExecution {
  readonly date: string;
  readonly activityId: string;
  readonly quantity: number;
  readonly executedByName: string;
}

export interface SheetLocal {
  readonly activityId: string;
  /** O pavimento — com a torre à frente quando os locais são de mais de uma. */
  readonly label: string;
  readonly towerName: string;
  readonly floorName: string;
}

export interface SheetExecutionRow {
  readonly date: string;
  readonly activityId: string;
  readonly localLabel: string;
  /** Soma do dia na frente: duas pessoas no mesmo pavimento são uma execução. */
  readonly quantity: number;
  readonly result: DayInspectionResult;
  /** As NCs do dia nesta frente que seguem de pé. Só com `REPROVADO`. */
  readonly ncNumbers: readonly number[];
}

/** A célula do item num pavimento: a data em que se aprovou e as NCs que ainda valem. */
export interface SheetItemCell {
  readonly approvedOn?: string;
  readonly ncNumbers: readonly number[];
}

export interface SheetItemRow {
  readonly itemId: string;
  readonly position: number;
  readonly label: string;
  readonly method?: string;
  readonly tolerance?: string;
  /** Por frente. Pavimento sem inspeção tem a célula vazia — e vazia é "ainda não". */
  readonly cells: Readonly<Record<string, SheetItemCell>>;
}

export interface SheetLocalSituation {
  readonly activityId: string;
  readonly status: LocalStatus;
  /** Os itens sem aprovação — o que falta inspecionar. Só com `PENDENTE`. */
  readonly missingItems: readonly string[];
  /** As NCs que seguem de pé neste pavimento, de qualquer item. */
  readonly ncNumbers: readonly number[];
}

/**
 * A amostragem: quantos dos pavimentos executados foram inspecionados, contra a meta.
 *
 * A meta é a da ficha (`samplingPct`) e o sistema não sorteia nada (RN-299): ele conta e
 * **só alerta quando está abaixo** — acima, ou sem meta declarada, não há o que dizer.
 */
export interface SheetSampling {
  readonly executedLocals: number;
  readonly inspectedLocals: number;
  /** A fração inspecionada, em por cento inteiro. Zero sem pavimento executado. */
  readonly percent: number;
  readonly targetPct?: number;
  readonly belowTarget: boolean;
}

/**
 * Quantos pavimentos ainda precisam ser inspecionados para chegar à meta — o que a tela
 * diz junto do alerta, para que ele seja um número a cumprir e não só uma cor.
 *
 * Zero sem meta, sem pavimento executado, ou já na meta.
 */
export function localsMissingForTarget(sampling: SheetSampling): number {
  if (sampling.targetPct === undefined || sampling.executedLocals === 0) return 0;
  const needed = Math.ceil((sampling.targetPct * sampling.executedLocals) / 100);
  return Math.max(0, needed - sampling.inspectedLocals);
}

export interface ServiceQualitySheet {
  readonly locals: readonly SheetLocal[];
  readonly executions: readonly SheetExecutionRow[];
  readonly items: readonly SheetItemRow[];
  readonly situations: readonly SheetLocalSituation[];
  readonly sampling: SheetSampling;
  /** "Considerações": os pavimentos executados e ainda pendentes de inspeção. */
  readonly pending: readonly SheetLocal[];
  /** Quem executou, e quem inspecionou — o cabeçalho da ficha. */
  readonly executedBy: readonly string[];
  readonly inspectedBy: readonly string[];
}

export interface BuildSheetInput {
  readonly checklist: Pick<ServiceChecklist, 'id' | 'items' | 'samplingPct'>;
  /** Toda frente da obra que o serviço pode ter usado — a ficha escolhe as que executaram. */
  readonly fronts: readonly SheetFront[];
  readonly executions: readonly SheetExecution[];
  /** As verificações **deste serviço** na obra, de qualquer dia. */
  readonly verifications: readonly ServiceVerification[];
  /** As NCs **deste serviço** na obra, canceladas inclusive — quem as descarta é a ficha. */
  readonly nonConformities: readonly NonConformity[];
}

const COMPARE = (a: string, b: string): number => a.localeCompare(b, 'pt-BR');

/** Torre, depois pavimento de baixo para cima — a ordem em que a obra sobe. */
function frontOrder(a: SheetFront, b: SheetFront): number {
  return (
    a.towerPosition - b.towerPosition ||
    COMPARE(a.towerName, b.towerName) ||
    a.level - b.level ||
    COMPARE(a.floorName, b.floorName)
  );
}

function sortedItems(items: readonly ChecklistItem[]): ChecklistItem[] {
  return [...items].sort((a, b) => a.position - b.position);
}

/** Do mais antigo para o mais novo: a última resposta é a que vale. */
function byDate(a: ServiceVerification, b: ServiceVerification): number {
  return COMPARE(a.date, b.date) || a.version - b.version;
}

/** As respostas desta ficha numa verificação — ela pode ter respondido outra do mesmo serviço. */
function answersOf(verification: ServiceVerification, checklistId: string) {
  return verification.checklists.find((item) => item.checklistId === checklistId)?.answers;
}

export function buildServiceQualitySheet(input: BuildSheetInput): ServiceQualitySheet {
  const { checklist } = input;
  const items = sortedItems(checklist.items);

  // As NCs que valem: a cancelada é a linha riscada e não entra (RN-221).
  const vivas = input.nonConformities.filter((nc) => nc.status !== 'CANCELLED');
  /**
   * As NCs vivas de uma resposta — desde a Fase 37 um item pode ter uma por medição (RN-474),
   * e a ligação mora na NC; a resposta de antes ainda guarda a dela.
   */
  const ncsDaResposta = (
    verification: ServiceVerification,
    answer: Pick<VerificationAnswer, 'itemId' | 'nonConformityId'>,
  ) => nonConformitiesOfAnswer(vivas, verification.id, checklist.id, answer);

  // ── Os locais: os pavimentos onde houve execução conferida ─────────────────────────
  const frontById = new Map(input.fronts.map((front) => [front.activityId, front]));
  const executedIds = [...new Set(input.executions.map((execution) => execution.activityId))];
  const executedFronts = executedIds
    .map((id) => frontById.get(id))
    .filter((front): front is SheetFront => front !== undefined)
    .sort(frontOrder);

  // Com mais de uma torre o pavimento sozinho confunde ("1º Pavimento" de qual?).
  const manyTowers = new Set(executedFronts.map((front) => front.towerName)).size > 1;
  const locals: SheetLocal[] = executedFronts.map((front) => ({
    activityId: front.activityId,
    towerName: front.towerName,
    floorName: front.floorName,
    label: manyTowers ? `${front.towerName} · ${front.floorName}` : front.floorName,
  }));
  const localOf = new Map(locals.map((local, index) => [local.activityId, { local, index }]));

  // As verificações que contam: desta ficha, nos pavimentos da ficha.
  const verifications = input.verifications
    .filter(
      (verification) =>
        localOf.has(verification.activityId) &&
        answersOf(verification, checklist.id as string) !== undefined,
    )
    .sort(byDate);
  const verificationsOf = (activityId: string) =>
    verifications.filter((verification) => verification.activityId === activityId);

  // ── Bloco 1: as execuções por dia ──────────────────────────────────────────────────
  const days = new Map<string, { date: string; activityId: string; quantity: number }>();
  for (const execution of input.executions) {
    if (!localOf.has(execution.activityId)) continue;
    const key = `${execution.date}|${execution.activityId}`;
    const day = days.get(key) ?? {
      date: execution.date,
      activityId: execution.activityId,
      quantity: 0,
    };
    days.set(key, { ...day, quantity: day.quantity + execution.quantity });
  }

  const executions: SheetExecutionRow[] = [...days.values()]
    .sort(
      (a, b) =>
        COMPARE(a.date, b.date) ||
        (localOf.get(a.activityId)?.index ?? 0) - (localOf.get(b.activityId)?.index ?? 0),
    )
    .map((day) => {
      const verification = verificationsOf(day.activityId).find((item) => item.date === day.date);
      const answers =
        verification === undefined ? undefined : answersOf(verification, checklist.id);
      const rejected = answers?.filter((answer) => answer.result === 'REJECTED') ?? [];
      const ncNumbers =
        verification === undefined
          ? []
          : [
              ...new Set(
                rejected.flatMap((answer) =>
                  ncsDaResposta(verification, answer).map((nc) => nc.number),
                ),
              ),
            ].sort((a, b) => a - b);
      const result: DayInspectionResult =
        answers === undefined ? 'PENDENTE' : rejected.length > 0 ? 'REPROVADO' : 'APROVADO';
      return {
        date: day.date,
        activityId: day.activityId,
        localLabel: localOf.get(day.activityId)!.local.label,
        quantity: day.quantity,
        result,
        ncNumbers,
      };
    });

  // ── Bloco 2: itens × pavimentos ────────────────────────────────────────────────────
  // O estado de cada item em cada pavimento, lendo as verificações do mais antigo ao mais
  // novo: a data de aprovação é a da **última** que aprovou, e as NCs são todas as que
  // seguem de pé — a reprovada num dia e aprovada noutro mostra as duas, que é o histórico.
  interface ItemState {
    approvedOn?: string;
    latest?: 'APPROVED' | 'REJECTED' | 'NOT_APPLICABLE';
    ncNumbers: number[];
  }
  const states = new Map<string, ItemState>();
  const stateKey = (activityId: string, itemId: string) => `${activityId}|${itemId}`;

  for (const verification of verifications) {
    for (const answer of answersOf(verification, checklist.id) ?? []) {
      const key = stateKey(verification.activityId, answer.itemId);
      const state = states.get(key) ?? { ncNumbers: [] };
      state.latest = answer.result;
      if (answer.result === 'APPROVED') state.approvedOn = verification.date;
      for (const nc of ncsDaResposta(verification, answer)) {
        if (!state.ncNumbers.includes(nc.number)) state.ncNumbers.push(nc.number);
      }
      states.set(key, state);
    }
  }

  const itemRows: SheetItemRow[] = items.map((item) => {
    const cells: Record<string, SheetItemCell> = {};
    for (const local of locals) {
      const state = states.get(stateKey(local.activityId, item.id));
      if (state === undefined) continue;
      cells[local.activityId] = {
        ncNumbers: [...state.ncNumbers].sort((a, b) => a - b),
        ...(state.approvedOn === undefined ? {} : { approvedOn: state.approvedOn }),
      };
    }
    return {
      itemId: item.id,
      position: item.position,
      label: item.label,
      cells,
      ...(item.method === undefined ? {} : { method: item.method }),
      ...(item.tolerance === undefined ? {} : { tolerance: item.tolerance }),
    };
  });

  // ── Bloco 3: a situação de cada pavimento (RN-299) ─────────────────────────────────
  // A NC e a reprovação contam de **qualquer** item respondido no pavimento, inclusive o
  // que saiu da ficha depois: tirar o item da checklist não resolve a NC (RN-295), e a
  // situação diria "conforme" enquanto o bloco 1 segue mostrando o dia reprovado. O que
  // falta inspecionar, esse sim, é só o que a ficha tem hoje.
  const answeredIn = (activityId: string): ItemState[] =>
    [...states].flatMap(([key, state]) => (key.startsWith(`${activityId}|`) ? [state] : []));
  const situations: SheetLocalSituation[] = locals.map((local) => {
    const answered = answeredIn(local.activityId);
    const ncNumbers = [...new Set(answered.flatMap((state) => state.ncNumbers))].sort(
      (a, b) => a - b,
    );

    // "Não se aplica" satisfaz o item: a ficha respondida inteira não deixa pendência.
    const missing = items.filter((item) => {
      const latest = states.get(stateKey(local.activityId, item.id))?.latest;
      return latest !== 'APPROVED' && latest !== 'NOT_APPLICABLE';
    });
    const rejected = answered.some((state) => state.latest === 'REJECTED');

    // Não conforme vem antes de pendente: a NC de pé é o que a obra tem de resolver, e
    // esconder isso atrás de "falta inspecionar" mudaria a conversa.
    const status: LocalStatus =
      ncNumbers.length > 0 || rejected
        ? 'NAO_CONFORME'
        : missing.length > 0
          ? 'PENDENTE'
          : 'CONFORME';
    return {
      activityId: local.activityId,
      status,
      ncNumbers,
      missingItems: status === 'PENDENTE' ? missing.map((item) => item.label) : [],
    };
  });

  // ── A amostragem ───────────────────────────────────────────────────────────────────
  const inspectedLocals = locals.filter(
    (local) => verificationsOf(local.activityId).length > 0,
  ).length;
  const targetPct = checklist.samplingPct;
  const sampling: SheetSampling = {
    executedLocals: locals.length,
    inspectedLocals,
    percent: locals.length === 0 ? 0 : Math.round((inspectedLocals * 100) / locals.length),
    // Compara sem arredondar: 7 de 18 é 38,9% e está abaixo de 40%, ainda que arredonde a 39.
    belowTarget:
      targetPct !== undefined &&
      locals.length > 0 &&
      inspectedLocals * 100 < targetPct * locals.length,
    ...(targetPct === undefined ? {} : { targetPct }),
  };

  const statusOf = new Map(situations.map((situation) => [situation.activityId, situation.status]));
  const unique = (names: readonly string[]) => [...new Set(names)].sort(COMPARE);

  return {
    locals,
    executions,
    items: itemRows,
    situations,
    sampling,
    pending: locals.filter((local) => statusOf.get(local.activityId) === 'PENDENTE'),
    executedBy: unique(
      input.executions
        .filter((item) => localOf.has(item.activityId))
        .map((item) => item.executedByName),
    ),
    inspectedBy: unique(verifications.map((verification) => verification.verifiedByName)),
  };
}
